import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react'
import { streamOllamaGenerate } from '../ollama'
import { speak } from '../speech/speak'
import styles from './WordLookup.module.scss'
import CloseIcon from '@mui/icons-material/Close'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'

// Tap-a-word dictionary, usable from any view:
//
//   <WordLookupProvider lang="tur" langName="Turkish" localDict={map} voice={voice} rate={0.9}>
//     …  const { lookup } = useWordLookup();  onClick={(e) => lookup('çantam', e, 'Bu benim çantam.')}
//   </WordLookupProvider>
//
// Resolution order: the view's own dictionary (lesson vocab / glossary — instant),
// then the Mac Studio for meaning, part of speech, base form + ending, a grammar
// note and an example. Results are cached per word for the life of the provider.

const Ctx = createContext(null)
export const normWord = (s) => String(s || '').normalize('NFC').toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
const parseObj = (raw) => {
  let s = String(raw || '').replace(/```json/gi, '').replace(/```/g, '').trim()
  const a = s.indexOf('{'), b = s.lastIndexOf('}')
  if (a >= 0 && b > a) s = s.slice(a, b + 1)
  try { return JSON.parse(s) } catch { return null }
}

export function WordLookupProvider({ lang = 'tur', langName = 'Turkish', localDict, voice, rate, model = 'gemma4:26b', children }) {
  const [state, setState] = useState(null) // { word, x, y, context, entry, loading, error }
  const cacheRef = useRef(new Map())
  const serverUrl = process.env.REACT_APP_OLLAMA_URI
  const apiKey = process.env.REACT_APP_OLLAMA_KEY

  const lookup = useCallback(async (word, evt, context) => {
    const w = String(word || '').trim()
    if (!w) return
    const x = evt && typeof evt.clientX === 'number' ? evt.clientX : window.innerWidth / 2
    const y = evt && typeof evt.clientY === 'number' ? evt.clientY : 120
    const local = localDict && localDict.get ? localDict.get(normWord(w)) : null
    const cached = cacheRef.current.get(w)
    setState({ word: w, x, y, context, entry: cached || (local ? { meaning: local, local: true } : null), loading: !cached, error: '' })
    if (cached) return
    if (!serverUrl) { setState(s => (s && s.word === w ? { ...s, loading: false, error: local ? '' : 'No model configured.' } : s)); return }
    try {
      const prompt = `You are a concise ${langName} dictionary for an English-speaking beginner. Explain the word "${w}"${context ? ` as used in: "${context}"` : ''}.
Return ONLY JSON: {"meaning":"short English meaning AS USED HERE","pos":"part of speech (noun, verb, adjective, particle, suffix…)","base":"the dictionary / base form in ${langName}; if the word carries a suffix, ending or particle, show it briefly, e.g. 'çanta + -m (my)'","note":"ONE short English note about the grammar of this form, or empty","example":"ONE short, simple ${langName} sentence using the word","exampleEn":"its English translation"}`
      const raw = await streamOllamaGenerate({ serverUrl, apiKey, body: { model, prompt, format: 'json', options: { temperature: 0.2, num_predict: 320 } } })
      const o = parseObj(raw) || {}
      const entry = { meaning: o.meaning || local || '', pos: o.pos || '', base: o.base || '', note: o.note || '', example: o.example || '', exampleEn: o.exampleEn || '' }
      cacheRef.current.set(w, entry)
      setState(s => (s && s.word === w ? { ...s, entry, loading: false } : s))
    } catch (e) {
      setState(s => (s && s.word === w ? { ...s, loading: false, error: 'Could not reach the Mac Studio.' } : s))
    }
  }, [localDict, serverUrl, apiKey, model, langName])

  const close = useCallback(() => setState(null), [])
  useEffect(() => {
    if (!state) return undefined
    const onKey = (e) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [state, close])

  return (
    <Ctx.Provider value={{ lookup, close }}>
      {children}
      {state && <WordPopover state={state} voice={voice} rate={rate} onClose={close} />}
    </Ctx.Provider>
  )
}

export function useWordLookup() {
  return useContext(Ctx) || { lookup: () => {}, close: () => {} }
}

function WordPopover({ state, voice, rate, onClose }) {
  const W = 300, pad = 12
  const left = Math.max(pad, Math.min(window.innerWidth - W - pad, state.x - W / 2))
  const flip = state.y > window.innerHeight * 0.62
  const pos = flip ? { left, bottom: window.innerHeight - state.y + 14 } : { left, top: state.y + 14 }
  const e = state.entry
  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.pop} style={pos} role="dialog" aria-label={`About ${state.word}`}>
        <div className={styles.head}>
          <button type="button" className={styles.word} onClick={() => speak(state.word, voice, rate)} title="Hear it">
            {state.word} <VolumeUpIcon fontSize="inherit" />
          </button>
          <button type="button" className={styles.close} onClick={onClose} title="Close"><CloseIcon fontSize="inherit" /></button>
        </div>
        {e && e.meaning ? <div className={styles.meaning}>{e.meaning}</div> : null}
        {e && (e.pos || e.base) ? (
          <div className={styles.metaRow}>
            {e.pos ? <span className={styles.pill}>{e.pos}</span> : null}
            {e.base ? <span className={styles.base}>base: <b>{e.base}</b></span> : null}
          </div>
        ) : null}
        {e && e.note ? <div className={styles.note}>{e.note}</div> : null}
        {e && e.example ? (
          <div className={styles.example} onClick={() => speak(e.example, voice, rate)} title="Hear the example">
            <span className={styles.exTr}>{e.example}</span>
            {e.exampleEn ? <span className={styles.exEn}>{e.exampleEn}</span> : null}
          </div>
        ) : null}
        {state.loading ? <div className={styles.loading}>{e ? 'adding detail…' : 'looking it up…'}</div> : null}
        {state.error && !state.loading ? <div className={styles.error}>{state.error}</div> : null}
      </div>
    </>
  )
}
